// phoneCountries.ts — Länderliste des Telefonfelds: ISO-Code (für die Flagge),
// deutscher Ländername (für die Suche) und Ländervorwahl.
//
// Die Vorwahlen kommen aus @kmvs/forms-core (DIAL_CODE_BY_ISO) — dieselbe
// Tabelle, mit der der Kern eingefügte Nummern zerlegt. Die deutschen
// Ländernamen kommen aus nationalities.json, das die App ohnehin für Land und
// Staatsangehörigkeit nutzt. Zwei Listen wären zwei Wahrheiten.

import { DIAL_CODE_BY_ISO, DEFAULT_DIAL_CODE } from "@kmvs/forms-core";
import { NATIONALITIES } from "./nationalities";

export interface PhoneCountry {
  /** ISO 3166-1 alpha-2, z. B. "DE" — Schlüssel für das Flaggen-Icon. */
  code: string;
  /** Deutscher Ländername, z. B. "Deutschland". */
  name: string;
  /** Ländervorwahl ohne "+", z. B. "49". */
  dial: string;
}

/**
 * Vorbelegung Deutschland; Österreich und die Schweiz stehen direkt darunter
 * (Spezifikation PO). Der Rest folgt alphabetisch nach deutschem Ländernamen —
 * die Reihenfolge, in der die Liste ohne Suchbegriff erscheint.
 */
const OBEN = ["DE", "AT", "CH"];

const alle: PhoneCountry[] = NATIONALITIES.filter((n) => DIAL_CODE_BY_ISO[n.code]).map(
  (n) => ({ code: n.code, name: n.country, dial: DIAL_CODE_BY_ISO[n.code] }),
);

export const PHONE_COUNTRIES: PhoneCountry[] = [
  ...OBEN.map((c) => alle.find((l) => l.code === c)).filter((l): l is PhoneCountry => !!l),
  ...alle
    .filter((l) => !OBEN.includes(l.code))
    .sort((a, b) => a.name.localeCompare(b.name, "de")),
];

export const DEFAULT_PHONE_COUNTRY: PhoneCountry =
  PHONE_COUNTRIES.find((l) => l.dial === DEFAULT_DIAL_CODE) ?? PHONE_COUNTRIES[0];

/**
 * Aus einer Vorwahl folgt KEIN eindeutiges Land: +1 tragen die USA, Kanada und
 * die halbe Karibik, +44 auch Jersey/Guernsey/Isle of Man, +7 Russland und
 * Kasachstan. Ohne diese Tabelle gewönne der alphabetisch erste Treffer — eine
 * eingefügte US-Nummer zeigte dann die Flagge von Antigua und Barbuda, +7 die
 * von Kasachstan. Sichtbar falsch zu einer korrekt erkannten Nummer.
 *
 * Deshalb je mehrfach belegter Vorwahl ein benanntes Hauptland; der Nutzer kann
 * im Dropdown weiterhin korrigieren.
 */
const HAUPTLAND: Readonly<Record<string, string>> = {
  "1": "US",
  "7": "RU",
  "39": "IT",
  "44": "GB",
  "212": "MA",
  "262": "RE",
  "358": "FI",
  "590": "GP",
  "599": "CW",
};

/**
 * Land zu einer Vorwahl — Hauptland zuerst, sonst der erste Treffer in der
 * Reihenfolge oben, damit "+49" Deutschland und "+41" die Schweiz ist.
 */
export function findPhoneCountry(dial: string): PhoneCountry | undefined {
  const d = String(dial).replace(/\D/g, "");
  const haupt = HAUPTLAND[d];
  if (haupt) {
    const treffer = PHONE_COUNTRIES.find((l) => l.code === haupt && l.dial === d);
    if (treffer) return treffer;
  }
  return PHONE_COUNTRIES.find((l) => l.dial === d);
}

const norm = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * Suche über Ländername UND Vorwahl: „Öster" findet Österreich, „43" ebenso —
 * mit und ohne führendes „+". Leerer Begriff → die volle Liste (kein Cap), die
 * Liste ist scrollbar.
 */
export function filterPhoneCountries(query: string): PhoneCountry[] {
  const q = query.trim().replace(/^\+/, "");
  if (!q) return PHONE_COUNTRIES;
  const nq = norm(q);
  return PHONE_COUNTRIES.filter(
    (l) => norm(l.name).includes(nq) || l.dial.startsWith(nq),
  );
}
